import {
  DatesRegularOpeningHours,
  StockProductAvailabilityPickupOrganizationUnit,
} from "@springtree/eva-services-core";
import classNames from "classnames";
import { useTranslations } from "next-intl";
import { Dispatch, Fragment, SetStateAction, useMemo } from "react";
import { DateTime } from "ts-luxon";

import IconClock from "@/icons/icon-clock";
import IconPickUp from "@/icons/icon-pick-up";
import { ApiConstants } from "~/shared/api/api-constants.class";
import { ErrorResponse } from "~/shared/api/error-response.class";
import { Coords } from "~/shared/coords.class";
import { AbstractShop } from "~/shared/shipping/abstract-shop.interface";
import { StoresModalContext } from "~/shared/stores/stores-modal";
import $fetch from "~/utils/fetch-utils";
import { WoosmapSuggestion } from "~/utils/woosmap-utils";

import Dialog from "../common/dialog";
import Icon from "../common/icon";
import { SearchedCity } from "./pickup-points-wrapper";
import styles from "./stores-modal.module.scss";
import StoresSelection from "./stores-selection";

export const defaultLocation = { lat: undefined, lng: undefined };

export type ScrollGradient = { top: boolean; bot: boolean };

function groupObjectsByAttributes(
  objects: any[],
  attr1: string,
  attr2: string,
  locale?: string
): Map<string, string[]> {
  return objects.reduce((map, obj) => {
    const key = obj[attr1] + " - " + obj[attr2];
    if (!map.has(key)) {
      map.set(key, []);
    }
    // when store has OpeningHours it comes with date string
    // else calculate based on weekday
    const todayDto = DateTime.local({
      locale,
    });
    const dto = obj.Date
      ? DateTime.fromISO(obj.Date, {
          locale,
        })
      : todayDto.plus({
          day: obj.DayOfWeek - todayDto.day,
        });
    map.get(key).push(
      dto.toLocaleString({
        weekday: "short",
        locale,
      })
    );

    return map;
  }, new Map());
}

export function getOpeningHours(opening: DatesRegularOpeningHours[], locale?: string, label?: string) {
  const openDays = opening
    .filter((day) => !day.Closed)
    .sort((a, b) => a.DayOfWeek - b.DayOfWeek)
    .map((day) => ({
      ...day,
      OpeningTime: day.OpeningTime.length > 5 ? day.OpeningTime.slice(0, -3) : day.OpeningTime,
      ClosingTime: day.ClosingTime.length > 5 ? day.ClosingTime.slice(0, -3) : day.ClosingTime,
    }));
  const openDaysGroupedByTime = groupObjectsByAttributes(openDays, "OpeningTime", "ClosingTime", locale);

  if (openDaysGroupedByTime.size > 0) {
    let openingDaysText: string[] = [];
    openDaysGroupedByTime.forEach((value, key) => {
      openingDaysText.push(`${value.join(" ")} ${key}`);
    });

    return (
      <div className={styles.hours}>
        <p className={styles.hoursLabel}>{label}</p>
        <div className={styles.hoursContent}>
          <Icon icon={IconClock} width={16} height={16} />
          {openingDaysText.map((el, i) => (
            <Fragment key={i}>
              {i != 0 && "|"}
              <p>{el}</p>
            </Fragment>
          ))}
        </div>
      </div>
    );
  } else {
    return <></>;
  }
}

function toRadians(degrees: number): number {
  return (degrees * Math.PI) / 180;
}

export function calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371; // Radius of the Earth in kilometers
  const dLat = toRadians(lat2 - lat1);
  const dLon = toRadians(lon2 - lon1);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c;
}

export async function reverseGeocode(lat_lng: string) {
  const args = {
    key: process.env.NEXT_PUBLIC_WOOSMAP_API_KEY,
    latlng: lat_lng,
  };

  return (await $fetch(`${ApiConstants.externalEndpoints.woosmap.geocode}?${buildQueryString(args)}`).json()) as {
    results: WoosmapSuggestion[];
  };
}

export function buildQueryString(params: any) {
  const queryStringParts = [];

  for (let key in params) {
    if (params.hasOwnProperty(key)) {
      let value = params[key];
      queryStringParts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
    }
  }
  return queryStringParts.join("&");
}

type StoresModalProps = {
  stores: AbstractShop[];
  context?: StoresModalContext;
  open: boolean;
  searchCity?: string;
  locationPos: Coords;
  filterAvailableOnly?: boolean;
  onClose: () => void;
  onSearchedCity?: Dispatch<SetStateAction<SearchedCity>>;
  onStoreConfirmation?: (store: StockProductAvailabilityPickupOrganizationUnit | undefined) => void;
  setLocationPos: Dispatch<SetStateAction<Coords>>;
  setFilterAvailableOnly?: Dispatch<SetStateAction<boolean>>;
  setPudoId?: (pudoId: string) => void;
  errorMessage?: ErrorResponse | null;
  enabled?: boolean;
  isLoading?: boolean;
  inModal?: boolean;
  inPickup?: boolean;
  inPDP?: boolean;
};

export default function StoresModal(props: StoresModalProps) {
  const t = useTranslations();

  const inPickUp = useMemo(() => props.context === StoresModalContext.pickUpStores, [props.context]);

  return (
    <Dialog
      isOpen={props.open}
      title={
        <>
          <Icon icon={IconPickUp} />
          <span>{t(inPickUp ? "generic.checkout.selectpickup" : "generic.selectstore")}</span>
        </>
      }
      onClose={props.onClose}
      customStyles={{
        wrapper: classNames(styles.overrideModal, styles.wrapper),
        panel: classNames(styles.overrideModal, styles.panel),
        content: classNames(styles.overrideModal, styles.content),
      }}
    >
      <StoresSelection {...props} inModal inPDP={props.inPDP} />
    </Dialog>
  );
}
